import { usePostApiV1AccountsFinishCreatingMutation } from '../../../authApi'
import { useCallback } from 'react'
import { SignupFields, SignupStep, authSlice } from '../../../authSlice'
import { useDispatch, useSelector } from 'react-redux'
import { logger } from 'helpers/logger'
import { FinishCreatingAccountParams } from '@wellesley/openapi'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useAppsNavigate } from '@wellesley/router'
import { toast } from 'react-toastify'
import { getErrorMsg } from '@wellesley/error-kit'
import { ServerApiError } from '@wellesley/openapi/helpers'

const useSubmit = () => {
  const dispatch = useDispatch()

  const navigate = useAppsNavigate()
  const [finishCreating] = usePostApiV1AccountsFinishCreatingMutation()

  const submit = useCallback(
    async (params: FinishCreatingAccountParams) => {
      try {
        await finishCreating({ finishCreatingAccountParams: params }).unwrap()
      } catch (e) {
        const error = (e as { data: ServerApiError })?.data

        switch (error?.errorCode) {
          case 'UserNameExists': {
            navigate('/signup/update-username/:hash', {
              props: { hash: params.hash },
            })
            return
          }
          case 'EmailExists': {
            toast.error(
              'Email already in use. Sign in or use another email to continue',
            )
            dispatch(authSlice.actions.setSignupStep('signup'))
            return
          }
          default:
            toast.error(getErrorMsg(e as FetchBaseQueryError))
            logger.error(JSON.stringify(error))
            return
        }
      }
    },
    [finishCreating, navigate, dispatch],
  )
  return { submit }
}

export const useFinishSignup = () => {
  const dispatch = useDispatch()

  const signupFields = useSelector(authSlice.selectors.selectSignupFields)

  const { submit } = useSubmit()

  const open = (step: SignupStep) =>
    dispatch(authSlice.actions.setSignupStep(step))

  const finishSignup = useCallback(
    async (newFields: SignupFields) => {
      dispatch(authSlice.actions.updateSignupFields(newFields))
      const { hash, nextStep } = {
        ...signupFields,
        ...newFields,
      }

      switch (true) {
        case !hash:
          return open('signup')
        case nextStep === 'EMAIL':
          return open('passCode')
        case nextStep === 'CAPTCHA':
          return open('captcha')
        case nextStep === 'REASON':
          return open('approval')
        case nextStep === 'ADMIN': {
          await submit({ hash })
          return open('approval')
        }
        case nextStep === 'USER': {
          await submit({ hash })
        }
      }
    },
    [submit],
  )

  return { finishSignup }
}
